import React from "react";
import { OfferFile } from "../../types";
import { analyticsService } from "../../services/analyticsService";
import { DocumentIcon, PaperClipIcon } from "../icons";
import { getApiUrl } from "../../services/apiClient";

interface OfferFilesSectionProps {
  offerFiles: OfferFile[];
  offerToken: string;
}

interface FileDownloadCardProps {
  offerFile: OfferFile;
  offerToken: string;
}

const FileDownloadCard: React.FC<FileDownloadCardProps> = ({
  offerFile,
  offerToken,
}) => {
  const { file } = offerFile;

  const getFileIcon = (fileType: string): React.ReactElement => {
    return (
      <DocumentIcon
        className="w-8 h-8"
        style={{ color: "var(--gradient-start, #64748b)" }}
      />
    );
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleDownload = async () => {
    try {
      // 1. Zapisz event w analityce
      if (analyticsService.isSessionActive()) {
        await fetch(
          `${getApiUrl()}/public/offers/${offerToken}/files/${offerFile.id}/download-event`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              sessionId: "",
            }),
          }
        ).catch((error) => {
          console.warn("Analytics tracking failed:", error);
          // Nie blokuj pobierania przy błędzie analityki
        });
      }

      // 2. Pobierz/otwórz plik
      if (file.fileType.toLowerCase().includes("pdf")) {
        // PDF - otwórz w nowej karcie
        window.open(file.fileUrl, "_blank");
      } else {
        // XLS/DOC - pobierz plik
        const link = document.createElement("a");
        link.href = file.fileUrl;
        link.download = file.originalFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback - otwórz link bezpośrednio
      window.open(file.fileUrl, "_blank");
    }
  };

  return (
    <div
      onClick={handleDownload}
      className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
    >
      <div className="flex items-start space-x-4 w-full">
        <div className="flex-shrink-0 mt-1">{getFileIcon(file.fileType)}</div>
        <div className="text-left w-full">
          <h4 className="font-medium text-gray-900 text-left">
            {file.displayName}
          </h4>
          <p className="text-sm text-gray-600 text-left">
            {offerFile.customDescription || file.description}
          </p>
          <p className="text-xs text-gray-400 text-left">
            {file.originalFileName} • {formatFileSize(file.fileSize)}
          </p>
        </div>
      </div>
    </div>
  );
};

const OfferFilesSection: React.FC<OfferFilesSectionProps> = ({
  offerFiles,
  offerToken,
}) => {
  if (!offerFiles || offerFiles.length === 0) {
    return null;
  }

  // Sortuj pliki według sortOrder
  const sortedFiles = [...offerFiles].sort(
    (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
  );

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold mb-4 flex items-center">
        <PaperClipIcon
          className="w-5 h-5 mr-2"
          style={{ color: "var(--gradient-start, #64748b)" }}
        />
        Pliki do pobrania
      </h3>
      <div className="space-y-3">
        {sortedFiles.map((offerFile) => (
          <FileDownloadCard
            key={offerFile.id}
            offerFile={offerFile}
            offerToken={offerToken}
          />
        ))}
      </div>
    </div>
  );
};

export default OfferFilesSection;
